import React, { useEffect, useCallback, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Form, Loader, Dimmer, Header } from 'semantic-ui-react';
import AddPaymentSourceModal from './AddPaymentSourceModal';
import PaymentInformationHeader from './PaymentInformationHeader';
import PaymentSources from './PaymentSources';
import EmailAddress from './EmailAddress';

const MultiPaymentInformation = ({ policies, payment, onPaymentInformationUpdate, allowMultipleEmailAddresses, defaultEmailAddress, actions, bypassEmailAddressCheck, emailAddressOptions }) => {
	const [sources, setSources] = useState([]);

	const totalAmount = useMemo(() => policies.reduce((acc, p) => acc + p.amount, 0), [policies]);
	const remainingAmount = useMemo(() => totalAmount - sources.reduce((acc, s) => acc + Object.values(s.amounts).reduce((acc2, v) => acc2 + v, 0), 0), [sources, totalAmount]);

	const canAdd = useMemo(() => remainingAmount > 0, [remainingAmount]);

	const [addPaymentSourceModalOpen, setAddPaymentSourceModalOpen] = useState(false);

	const [emailAddress, setEmailAddress] = useState(defaultEmailAddress || '');

	const onAddPaymentSourceModalClose = useCallback(() => {
		setAddPaymentSourceModalOpen(false);
	}, []);

	useEffect(() => {
		setAddPaymentSourceModalOpen(true);
	}, []);

	const onPaymentSourceAdd = useCallback(
		(source) => {
			const currentAmounts = {};
			policies.forEach((p) => {
				currentAmounts[p.displayName] = sources.reduce((acc, s) => acc + (s.amounts[p.displayName] || 0), 0);
			});

			source.amounts = {};
			policies.forEach((policy, i) => {
				source.amounts[policy.displayName] = policy.amount - (currentAmounts[policy.displayName] || 0);
			});

			setSources([...sources, source]);
		},
		[policies, sources]
	);

	const onRemoveSource = useCallback(
		(source) => {
			setSources([...sources.filter((s) => s !== source)]);
		},
		[sources]
	);

	useEffect(() => {
		sources.forEach((source) => {
			source.emailAddress = emailAddress;
		});

		onPaymentInformationUpdate([...sources]);
	}, [emailAddress, onPaymentInformationUpdate, sources]);

	const onAmountChange = useCallback(
		(e, source, amount, amountName) => {
			source.amounts[amountName] = amount;
			setSources([...sources]);
		},
		[sources]
	);

	const { settings, auth, banks } = payment;
	const { loading: isSettingsLoading } = settings;
	const { loading: isAuthLoading } = auth;

	return (
		<div>
			{(isSettingsLoading || isAuthLoading) && (
				<Dimmer active>
					<Loader size='massive'>Loading</Loader>
				</Dimmer>
			)}

			<AddPaymentSourceModal open={addPaymentSourceModalOpen} onClose={onAddPaymentSourceModalClose} onPaymentSourceAdd={onPaymentSourceAdd} settings={settings} auth={auth} banks={banks} actions={actions} policies={policies} />

			<PaymentInformationHeader policies={policies} sources={sources} includeRemaining />

			<Header>Payment Methods</Header>

			{sources.length <= (settings.numberOfPaymentMethodsToAllowOnSplitPayments || settings.numberOfPaymentMethodsToAllowOnSplitPayments === 0) && (
				<Form widths='equal' error>
					<Form.Group>
						<Form.Button onClick={() => setAddPaymentSourceModalOpen(true)} disabled={!canAdd}>
							Add Payment Method
						</Form.Button>
					</Form.Group>
				</Form>
			)}
			<PaymentSources sources={sources} isEdit onAmountChange={onAmountChange} onRemoveSource={onRemoveSource} userEmailAddress={defaultEmailAddress} actions={actions}  policies={policies} />
			<Form widths='equal' error>
				<EmailAddress emailAddress={emailAddress} allowMultipleEmailAddresses={allowMultipleEmailAddresses} required={!bypassEmailAddressCheck} onChange={setEmailAddress} emailAddressOptions={emailAddressOptions} />
			</Form>
		</div>
	);
};

MultiPaymentInformation.propTypes = {
	policies: PropTypes.array.isRequired,
	payment: PropTypes.object.isRequired,
	onPaymentInformationUpdate: PropTypes.func.isRequired,
	bypassEmailAddressCheck: PropTypes.bool,
};

export default MultiPaymentInformation;
